import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Filter, Plus } from "lucide-react";
import AktivitaDetail from "./AktivitaDetail";
import NovaAktivita from "./NovaAktivita";
import { SwipeAktivita, useAktivitaSwipe } from "./AktivitaSwipe";
import { useAuth } from "../context/AuthContext";
import { canEditAktivita } from "../lib/nav";
import { barvaOsoby } from "../lib/osoby";
import { fmtTime } from "../lib/format";
import api from "../lib/api";
import OsobaKolecko from "./OsobaKolecko";
import { useObnovaPriNavratu } from "../lib/obnova";

/**
 * Kalendář na telefonu podle handoffu `design_handoff_kalendar_mobil` (řada
 * 14c). Jedna obrazovka, dva režimy: denní osa 7–19 h, roztažená podle
 * událostí dne (výchozí, pro
 * technika) a měsíc se seznamem dne (pro majitele). Přepíná je jedna
 * pilulka v hlavičce pojmenovaná podle toho, co je vidět.
 *
 * Barva = stav: hotovo zelená, otevřené modrá, po termínu červená. Osoba je
 * vždy jen tečka, nikdy barva bloku.
 */

const DNY = ["po", "út", "st", "čt", "pá", "so", "ne"];
const DNY_DLOUHE = ["neděle", "pondělí", "úterý", "středa", "čtvrtek", "pátek", "sobota"];
// 4. pád pro „Na středu 17. 9. nemáš nic."
const DNY_4 = ["neděli", "pondělí", "úterý", "středu", "čtvrtek", "pátek", "sobotu"];
const MESICE = ["leden", "únor", "březen", "duben", "květen", "červen",
  "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];

const OSA_OD = 7, OSA_DO = 19, HODINA_PX = 56;
const MIN_BLOK = 44, KROK_MIN = 15;
// Přesun události: nejdřív ji podržet. Prosté přejetí prstem jen posouvá
// stránku — jinak by se události při listování kalendářem tiše přesouvaly.
const DRZET_MS = 500;

const STAV = {
  HOTOVA: { c: "#17876a", bg: "#eaf6f1" },
  OTEVRENA: { c: "#2c6ecb", bg: "#eef4fd" },
  PO_TERMINU: { c: "#d93a2b", bg: "#fdeae7" },
};
const stavKey = (a) => (a.po_terminu ? "PO_TERMINU" : a.stav === "HOTOVA" ? "HOTOVA" : "OTEVRENA");

const den0 = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const plusDny = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const stejnyDen = (a, b) => den0(a).getTime() === den0(b).getTime();
const zacatekTydne = (d) => plusDny(den0(d), -((d.getDay() + 6) % 7));
const zacatekMrizky = (d) => zacatekTydne(new Date(d.getFullYear(), d.getMonth(), 1));
const velke = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const pocet = (n, j, d, p) => `${n} ${n === 1 ? j : n >= 2 && n <= 4 ? d : p}`;

/** „Dnes · čtvrtek 10. 9." / „Zítra · pátek 11. 9." / „Středa 17. 9." */
function popisDne(d) {
  const dnes = den0(new Date());
  const zaklad = `${DNY_DLOUHE[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}.`;
  if (stejnyDen(d, dnes)) return `Dnes · ${zaklad}`;
  if (stejnyDen(d, plusDny(dnes, 1))) return `Zítra · ${zaklad}`;
  return velke(zaklad);
}

const minutOd = (iso, od) => { const d = new Date(iso); return (d.getHours() - od) * 60 + d.getMinutes(); };

/** Osa je 7–19 h, ale roztáhne se, když má den události dřív nebo později
 *  (ranní výjezd, večerní schůzka) — zaplnění dne musí být vidět celé. */
function rozsahOsy(den, aktivity) {
  let od = OSA_OD, doH = OSA_DO;
  aktivity.forEach((a) => {
    const s = new Date(a.date_start), e = a.date_end ? new Date(a.date_end) : null;
    if (!e || e <= s || !stejnyDen(s, den)) return;
    od = Math.min(od, s.getHours());
    doH = Math.max(doH, stejnyDen(e, den) ? e.getHours() + (e.getMinutes() > 0 ? 1 : 0) : 24);
  });
  return { od, doH: Math.min(24, doH) };
}
const zaokrouhli = (min) => Math.round(min / KROK_MIN) * KROK_MIN;

export default function KalendarMobil() {
  const { user } = useAuth();
  const [sel, setSel] = useState(() => den0(new Date()));
  const [rezim, setRezim] = useState("osa");
  const [mesicSbaleny, setMesicSbaleny] = useState(false);
  const [mesic, setMesic] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [filtrOtevreny, setFiltrOtevreny] = useState(false);
  const [oblast, setOblast] = useState(null);
  const [osoby, setOsoby] = useState([]);
  const [members, setMembers] = useState([]);
  const [acts, setActs] = useState([]);
  const [poTerminu, setPoTerminu] = useState([]);
  // Z Domů („Vyřešit →") přichází ?jen=po-terminu — seznam rovnou rozbalený.
  const [poTerminuRozbalene, setPoTerminuRozbalene] = useState(
    () => new URLSearchParams(window.location.search).get("jen") === "po-terminu");
  const [detailId, setDetailId] = useState(null);
  const [editAct, setEditAct] = useState(null);
  const [nova, setNova] = useState(null);       // { defaultDate, defaultStart }

  // Rozsah = 42 dní mřížky měsíce, ze kterých čerpá osa i měsíc, ať puntíky
  // v pásu dní a v mřížce ukazují totéž. Osa mimo aktuální měsíc si rozsah
  // posune sama přes `mesic`.
  const rozsah = useMemo(() => {
    const od = zacatekMrizky(mesic);
    return { od, do: plusDny(od, 42) };
  }, [mesic]);

  const load = useCallback(async () => {
    const [a, p] = await Promise.all([
      api.get("/api/aktivity", { params: { od: rozsah.od.toISOString(), do: rozsah.do.toISOString(), limit: 500 } }),
      api.get("/api/aktivity", { params: { stav: "OTEVRENA", po_terminu: true, limit: 200 } }),
    ]);
    setActs(a.data.items || []);
    setPoTerminu(p.data.items || []);
  }, [rozsah]);
  useEffect(() => { load(); }, [load]);
  useObnovaPriNavratu(load);
  useEffect(() => {
    api.get("/api/team")
      .then(({ data }) => setMembers((data.members || []).filter((m) => m.active !== false)))
      .catch(() => setMembers([]));
  }, []);

  // Vybraný den mimo načtený rozsah → posunout měsíc, rozsah se přenačte.
  useEffect(() => {
    if (sel < rozsah.od || sel >= rozsah.do) setMesic(new Date(sel.getFullYear(), sel.getMonth(), 1));
  }, [sel, rozsah]);

  const swipe = useAktivitaSwipe({ reload: load, onUpravit: setEditAct });

  const sedi = useCallback((a) =>
    (!oblast || a.oblast === oblast) &&
    (!osoby.length || (a.odpovedne_osoby || []).some((u) => osoby.includes(u))), [oblast, osoby]);
  const viditelne = useMemo(() => acts.filter(sedi), [acts, sedi]);
  const poTerminuVid = useMemo(() => poTerminu.filter(sedi), [poTerminu, sedi]);
  const vDen = useCallback((d) => viditelne.filter((a) => stejnyDen(new Date(a.date_start), d))
    .sort((x, y) => new Date(x.date_start) - new Date(y.date_start)), [viditelne]);
  const puntikyDne = (d) => {
    const s = new Set(vDen(d).map(stavKey));
    return ["PO_TERMINU", "OTEVRENA", "HOTOVA"].filter((k) => s.has(k));
  };

  const pocetFiltru = (oblast ? 1 : 0) + (osoby.length ? 1 : 0);
  const tyden = useMemo(() => Array.from({ length: 7 }, (_, i) => plusDny(zacatekTydne(sel), i)), [sel]);
  const dnyDne = vDen(sel);
  const smiPsat = (a) => canEditAktivita(user, a.oblast);

  const prepni = () => {
    if (rezim === "osa") { setRezim("mesic"); setMesic(new Date(sel.getFullYear(), sel.getMonth(), 1)); }
    else { setRezim("osa"); setSel(den0(new Date())); }
  };
  const posunTyden = (n) => setSel(plusDny(sel, 7 * n));
  const posunMesic = (n) => setMesic(new Date(mesic.getFullYear(), mesic.getMonth() + n, 1));

  // Swipe doleva/doprava po pásu dní a mřížce měsíce — tlačítka jsou záloha.
  const tah = useRef(null);
  const tahStart = (e) => { tah.current = { x: e.clientX, y: e.clientY }; };
  const tahKonec = (fn) => (e) => {
    if (!tah.current) return;
    const dx = e.clientX - tah.current.x, dy = e.clientY - tah.current.y;
    tah.current = null;
    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) fn(dx < 0 ? 1 : -1);
  };

  const podradek = (a) => [a.client_name, a.obchodni_pripad?.nazev, a.zarizeni_nazev, (a.osoby_jmena || [])[0]?.split(" ")[0]].filter(Boolean).join(" · ") || a.typ;

  const chip = (aktivni, extra = "") =>
    `cip tap h-[38px] shrink-0 pill border px-[13px] t-meta font-600 whitespace-nowrap inline-flex items-center gap-1.5 ${
      aktivni ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong"} ${extra}`;

  return (
    <div data-testid="kalendar-mobil" className="-mx-4 px-[18px] flex flex-col gap-2.5">
      {/* hlavička */}
      <div className="flex items-center gap-2 min-h-[44px]">
        <div className="min-w-0">
          <div className="t-nadpis text-ink whitespace-nowrap truncate" data-testid="kal-titulek">
            {velke(`${DNY_DLOUHE[sel.getDay()]} ${sel.getDate()}. ${sel.getMonth() + 1}.`)}
          </div>
          <div className="t-meta text-muted -mt-0.5">{MESICE[sel.getMonth()]} {sel.getFullYear()}</div>
        </div>
        <button onClick={prepni} data-testid="kal-rezim"
          className="tap ml-auto shrink-0 h-[38px] pill border border-line-strong bg-white px-3.5 t-meta font-600 text-sec">
          {rezim === "osa" ? "Dnes" : "Měsíc"}
        </button>
        <button onClick={() => setFiltrOtevreny((v) => !v)} data-testid="kal-filtr"
          className={chip(pocetFiltru > 0, "px-3")}>
          <Filter size={14} /> {pocetFiltru ? `Filtr · ${pocetFiltru}` : "Filtr"}
        </button>
      </div>

      {/* řada filtrů */}
      {filtrOtevreny && (
        <div className="flex gap-1.5 overflow-x-auto -mx-[18px] px-[18px] pb-0.5 [scrollbar-width:none]" data-testid="kal-filtry">
          {[[null, "Vše"], ["SERVIS", "Servis"], ["OBCHOD", "Obchod"]].map(([v, l]) => (
            <button key={l} data-on={oblast === v} onClick={() => setOblast(v)} className={chip(oblast === v)} data-testid={`kal-oblast-${l.toLowerCase()}`}>{l}</button>
          ))}
          <span className="w-px h-6 bg-line-strong my-2 mx-1 shrink-0" />
          <button data-on={!osoby.length} onClick={() => setOsoby([])} className={chip(!osoby.length)} data-testid="kal-osoby-vse">Celá firma</button>
          {members.map((m) => (
            <button key={m.id} data-on={osoby.includes(m.id)} data-testid={`kal-osoba-${m.id}`}
              onClick={() => setOsoby((o) => (o.includes(m.id) ? o.filter((x) => x !== m.id) : [...o, m.id]))}
              className={chip(osoby.includes(m.id), "pl-2")}>
              <OsobaKolecko id={m.id} name={m.name} url={m.photo_url} size={22} />
              {m.id === user?.id ? "Já" : (m.name || "").split(" ")[0]}
            </button>
          ))}
        </div>
      )}

      {/* režim měsíc: karta měsíce */}
      {rezim === "mesic" && (
        <div className="card px-2 pt-2.5" data-testid="kal-mesic"
          onPointerDown={tahStart} onPointerUp={tahKonec(posunMesic)}>
          <div className="flex items-center px-1 pb-1">
            <button onClick={() => posunMesic(-1)} className="h-9 w-9 rounded-full flex items-center justify-center text-sec" data-testid="kal-mesic-prev"><ChevronLeft size={18} /></button>
            <div className="flex-1 text-center t-telo font-700 text-ink">{velke(MESICE[mesic.getMonth()])} {mesic.getFullYear()}</div>
            <button onClick={() => posunMesic(1)} className="h-9 w-9 rounded-full flex items-center justify-center text-sec" data-testid="kal-mesic-next"><ChevronRight size={18} /></button>
          </div>
          <div className="grid grid-cols-7">
            {DNY.map((d) => <span key={d} className="text-center text-[11px] font-600 text-muted">{d}</span>)}
          </div>
          <div className="grid grid-cols-7">
            {(mesicSbaleny ? tyden : (() => {
              const od = zacatekMrizky(mesic);
              const posledni = new Date(mesic.getFullYear(), mesic.getMonth() + 1, 0);
              const radku = Math.ceil((Math.round((den0(posledni) - od) / 86400000) + 1) / 7);
              return Array.from({ length: radku * 7 }, (_, i) => plusDny(od, i));
            })()).map((d) => {
              const vybrany = stejnyDen(d, sel), dnes = stejnyDen(d, new Date());
              const mimo = d.getMonth() !== mesic.getMonth();
              return (
                <button key={d.getTime()} onClick={() => setSel(den0(d))} data-testid={`kal-den-${d.getDate()}-${d.getMonth()}`}
                  className="h-12 flex flex-col items-center justify-center gap-[3px]">
                  <span className={`h-[30px] w-[30px] rounded-full amount text-[14px] flex items-center justify-center ${
                    vybrany ? "bg-ink text-white font-700"
                      : dnes ? "border-[1.5px] border-accent text-accent font-700"
                        : mimo ? "text-[#c3c5c1]" : "text-ink"}`}>
                    {d.getDate()}
                  </span>
                  <span className="flex gap-[3px] h-1">
                    {puntikyDne(d).map((k) => <span key={k} className="h-1 w-1 rounded-full" style={{ background: STAV[k].c }} />)}
                  </span>
                </button>
              );
            })}
          </div>
          <button onClick={() => setMesicSbaleny((v) => !v)} data-testid="kal-mesic-sbalit"
            className="w-full h-[34px] flex items-center justify-center gap-1.5 text-[12px] font-600 text-muted">
            <span className="h-1 w-[38px] rounded-pill bg-line-strong" />
            {mesicSbaleny ? "Rozbalit měsíc" : "Sbalit"}
          </button>
        </div>
      )}

      {/* režim osa: pás dní */}
      {rezim === "osa" && (
        <div className="flex items-center gap-0.5 -mx-2" data-testid="kal-pas-dni"
          onPointerDown={tahStart} onPointerUp={tahKonec(posunTyden)}>
          <button onClick={() => posunTyden(-1)} className="w-7 h-16 flex items-center justify-center text-muted shrink-0" data-testid="kal-tyden-prev"><ChevronLeft size={16} /></button>
          <div className="flex-1 grid grid-cols-7">
            {tyden.map((d) => {
              const vybrany = stejnyDen(d, sel), dnes = stejnyDen(d, new Date());
              return (
                <button key={d.getTime()} onClick={() => setSel(den0(d))} data-testid={`kal-pas-${d.getDate()}`}
                  className={`h-16 rounded-[12px] flex flex-col items-center justify-center gap-[3px] ${vybrany ? "bg-ink text-white" : dnes ? "text-accent" : "text-ink"}`}>
                  <span className="text-[11px] font-600 opacity-70">{DNY[(d.getDay() + 6) % 7]}</span>
                  <span className="amount text-[16px] font-700">{d.getDate()}</span>
                  <span className="flex gap-[3px] h-[5px]">
                    {puntikyDne(d).map((k) => <span key={k} className="h-[5px] w-[5px] rounded-full" style={{ background: vybrany ? "#fff" : STAV[k].c }} />)}
                  </span>
                </button>
              );
            })}
          </div>
          <button onClick={() => posunTyden(1)} className="w-7 h-16 flex items-center justify-center text-muted shrink-0" data-testid="kal-tyden-next"><ChevronRight size={16} /></button>
        </div>
      )}

      {/* po termínu — karta s tečkou a akcí, nikdy plný červený pás */}
      {poTerminuVid.length > 0 && (() => {
        const nejstarsi = [...poTerminuVid].sort((x, y) => new Date(x.date_end) - new Date(y.date_end))[0];
        const k = new Date(nejstarsi.date_end);
        return (
          <>
            <div className="card px-4 py-3 flex items-center gap-2.5" data-testid="kal-po-terminu">
              <span className="h-2 w-2 rounded-full bg-danger shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-700 text-ink">{pocet(poTerminuVid.length, "událost", "události", "událostí")} po termínu</div>
                <div className="t-meta text-muted leading-[1.4]">nejstarší z {k.getDate()}. {k.getMonth() + 1}. ({nejstarsi.nazev})</div>
              </div>
              <button onClick={() => setPoTerminuRozbalene((v) => !v)} data-testid="kal-po-terminu-toggle"
                className="shrink-0 h-9 px-3 t-meta font-700 text-accent-hover">
                {poTerminuRozbalene ? "Skrýt" : "Zobrazit"}
              </button>
            </div>
            {poTerminuRozbalene && (
              <div className="space-y-2" data-testid="kal-po-terminu-seznam">
                {poTerminuVid.map((a) => (
                  <SwipeAktivita key={a.id} a={a} cas="datum" podradek={podradek}
                    onOpen={(x) => setDetailId(x.id)} smiUpravit={smiPsat(a)} {...swipe.swipeProps} />
                ))}
              </div>
            )}
          </>
        );
      })()}

      {rezim === "mesic" ? (
        <div className="space-y-2" data-testid="kal-seznam-dne">
          <div className="text-[15px] font-700 text-ink pt-1.5 px-0.5">{popisDne(sel)}</div>
          {dnyDne.map((a) => (
            <SwipeAktivita key={a.id} a={a} cas="cas" podradek={podradek}
              onOpen={(x) => setDetailId(x.id)} smiUpravit={smiPsat(a)} {...swipe.swipeProps} />
          ))}
          {dnyDne.length === 0 && (
            <div className="bg-white border border-dashed border-line-strong rounded-card p-4 flex flex-col items-start gap-2.5" data-testid="kal-prazdny-den">
              <div className="text-[14px] leading-[1.5] text-sec">
                Na {DNY_4[sel.getDay()]} {sel.getDate()}. {sel.getMonth() + 1}. nemáš nic. Přidej schůzku nebo servis.
              </div>
              <button onClick={() => setNova({ defaultDate: sel })} data-testid="kal-pridat-den"
                className="tap h-[38px] pill border border-line-strong bg-white px-3.5 t-meta font-600 text-sec">
                + Přidat na tenhle den
              </button>
            </div>
          )}
        </div>
      ) : (
        <Osa den={sel} aktivity={dnyDne} podradek={podradek} smiPsat={smiPsat} swipe={swipe}
          onOtevri={(a) => setDetailId(a.id)} onMezera={(start) => setNova({ defaultStart: start })}
          onPresun={async (a, start, end) => {
            await api.patch(`/api/aktivity/${a.id}`, { date_start: start.toISOString(), date_end: end.toISOString() });
            load();
          }} />
      )}

      {/* FAB — jediná oranžová akce na obrazovce */}
      <button onClick={() => setNova({ defaultDate: sel })} data-testid="kal-fab" aria-label="Nová aktivita"
        className="fixed right-[18px] bottom-[calc(100px_+_env(safe-area-inset-bottom))] z-30 h-14 w-14 rounded-full bg-accent text-white shadow-panel flex items-center justify-center">
        <Plus size={26} />
      </button>

      {nova && (
        <NovaAktivita defaultDate={nova.defaultDate || null} defaultStart={nova.defaultStart || null}
          onClose={() => setNova(null)} onSaved={() => { setNova(null); load(); }} />
      )}
      {detailId && (
        <AktivitaDetail id={detailId} onClose={() => setDetailId(null)}
          onEdit={(a) => { setDetailId(null); setEditAct(a); }} onChanged={load} />
      )}
      {editAct && (
        <NovaAktivita aktivita={editAct} onClose={() => setEditAct(null)}
          onSaved={() => { setEditAct(null); load(); }} />
      )}
      {swipe.supliky}
    </div>
  );
}

/** Denní osa (7–19 h, roztažená podle událostí): hodinové čáry, bloky
 *  událostí, klikací mezery, čára „teď". Událost se přesouvá po podržení. */
function Osa({ den, aktivity, podradek, smiPsat, swipe, onOtevri, onMezera, onPresun }) {
  const [tah, setTah] = useState(null);   // { id, posun (min) } u zvednuté události
  const drzeni = useRef(null);
  const osaRef = useRef(null);

  const { od: hodOd, doH } = rozsahOsy(den, aktivity);
  const konec = (doH - hodOd) * 60;
  const vyskaOsy = (doH - hodOd) * HODINA_PX;
  const minut = (iso) => minutOd(iso, hodOd);
  // Na osu patří, co má délku a začíná tento den. Zbytek (bez konce, celodenní)
  // jde do seznamu nad osou — nesmí zmizet jen proto, že se do osy nevejde.
  const naOse = (a) => a.date_end && new Date(a.date_end) > new Date(a.date_start)
    && minut(a.date_end) > 0 && minut(a.date_start) < konec;
  const casove = aktivity.filter(naOse);
  const bezCasu = aktivity.filter((a) => !naOse(a));

  const bloky = casove.map((a) => {
    const od = Math.max(0, minut(a.date_start));
    const doM = Math.min(konec, minut(a.date_end));
    const posun = tah?.id === a.id ? tah.posun : 0;
    return { a, od: od + posun, do: doM + posun, delka: doM - od };
  }).sort((x, y) => x.od - y.od);

  // Mezery ≥ 30 min mezi bloky (tah je ignoruje, počítá se z klidového stavu).
  const mezery = [];
  let kurzor = 0;
  for (const b of bloky.filter((x) => tah?.id !== x.a.id)) {
    if (b.od - kurzor >= 30) mezery.push({ od: kurzor, do: b.od });
    kurzor = Math.max(kurzor, b.do);
  }
  if (konec - kurzor >= 30) mezery.push({ od: kurzor, do: konec });

  const hhmm = (min) => `${String(Math.floor(min / 60) + hodOd).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
  const casOd = (min) => { const d = new Date(den); d.setHours(hodOd, 0, 0, 0); return new Date(d.getTime() + min * 60000); };

  const dnes = stejnyDen(den, new Date());
  const ted = minut(new Date().toISOString());

  // Zvednutou událost táhne prst — stránka se přitom nesmí posouvat. Jinak
  // (touch-action: pan-y) se normálně listuje, i když prst začne na události.
  useEffect(() => {
    const el = osaRef.current;
    if (!el) return undefined;
    const h = (e) => { if (drzeni.current?.zvednuto) e.preventDefault(); };
    el.addEventListener("touchmove", h, { passive: false });
    return () => el.removeEventListener("touchmove", h);
  }, []);

  const zacniTah = (b) => (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const d = { id: b.a.id, x: e.clientX, y: e.clientY, od: b.od, delka: b.delka, zvednuto: false, posun: 0,
      el: e.currentTarget, pointerId: e.pointerId };
    drzeni.current = d;
    if (!smiPsat(b.a)) return;
    d.timer = setTimeout(() => {
      if (drzeni.current !== d) return;
      d.zvednuto = true;
      d.y = d.posledniY ?? d.y;
      try { d.el.setPointerCapture(d.pointerId); } catch { /* starší prohlížeč */ }
      try { navigator.vibrate?.(12); } catch { /* bez vibrace */ }
      setTah({ id: d.id, posun: 0 });
    }, DRZET_MS);
  };
  const pohybTahu = (e) => {
    const d = drzeni.current; if (!d) return;
    d.posledniY = e.clientY;
    if (!d.zvednuto) {
      // Prst se hnul dřív, než se událost zvedla = listování, ne přesun.
      if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > 8) { clearTimeout(d.timer); drzeni.current = null; }
      return;
    }
    let posun = zaokrouhli((e.clientY - d.y) / HODINA_PX * 60);
    posun = Math.max(-d.od, Math.min(konec - d.delka - d.od, posun));
    if (posun !== d.posun) { d.posun = posun; setTah({ id: d.id, posun }); }
  };
  const konecTahu = (b) => async () => {
    const d = drzeni.current; drzeni.current = null;
    if (!d) return;
    clearTimeout(d.timer);
    setTah(null);
    if (!d.zvednuto) { onOtevri(b.a); return; }   // krátké klepnutí = detail
    if (!d.posun) return;
    const start = casOd(d.od + d.posun);
    await onPresun(b.a, start, new Date(start.getTime() + d.delka * 60000));
  };
  const zrusTah = () => {
    const d = drzeni.current; drzeni.current = null;
    if (d) clearTimeout(d.timer);
    setTah(null);
  };

  return (
    <div className="flex flex-col gap-2.5" data-testid="kal-osa">
      {bezCasu.length > 0 && (
        <div className="space-y-2">
          <div className="label-up text-muted px-0.5">Bez času · celý den</div>
          {bezCasu.map((a) => (
            <SwipeAktivita key={a.id} a={a} cas="cas" podradek={podradek}
              onOpen={() => onOtevri(a)} smiUpravit={smiPsat(a)} {...swipe.swipeProps} />
          ))}
        </div>
      )}

      <div className="card pt-3.5 pb-3.5 pr-3 relative">
        <div ref={osaRef} className="relative ml-14" style={{ height: vyskaOsy }}>
          {Array.from({ length: doH - hodOd + 1 }, (_, i) => (
            <div key={i} className="absolute left-0 right-0 border-t border-line-soft" style={{ top: i * HODINA_PX }}>
              <span className="absolute -left-[52px] -top-2 w-11 text-right amount text-[12px] text-muted">
                {String(hodOd + i).padStart(2, "0")}:00
              </span>
            </div>
          ))}

          {mezery.map((m) => (
            <button key={m.od} onClick={() => onMezera(casOd(m.od))} data-testid={`kal-mezera-${m.od}`}
              className="absolute left-1 right-0 rounded-[10px] border border-dashed border-line-strong text-[12px] font-600 text-muted-2 flex items-center justify-center hover:text-accent-hover hover:border-accent-hover"
              style={{ top: m.od / 60 * HODINA_PX + 1, height: (m.do - m.od) / 60 * HODINA_PX - 3 }}>
              {m.do - m.od >= 60 ? `+ ${hhmm(m.od)}–${hhmm(m.do)} volno` : "+"}
            </button>
          ))}

          {bloky.map((b) => {
            const k = STAV[stavKey(b.a)];
            const vyska = Math.max(MIN_BLOK, b.delka / 60 * HODINA_PX - 3);
            const plny = vyska >= 70;
            const hotova = b.a.stav === "HOTOVA";
            const tazeny = tah?.id === b.a.id;
            return (
              <div key={b.a.id} data-testid={`kal-blok-${b.a.id}`} data-zvednuty={tazeny || undefined}
                onPointerDown={zacniTah(b)} onPointerMove={pohybTahu} onPointerUp={konecTahu(b)} onPointerCancel={zrusTah}
                onContextMenu={(e) => e.preventDefault()}
                className={`absolute left-1 right-0 rounded-[10px] px-2.5 py-1.5 overflow-hidden flex flex-col justify-center gap-px
                  touch-pan-y select-none transition-[box-shadow,transform] duration-150 motion-reduce:transition-none
                  ${tazeny ? "shadow-panel z-[5] scale-[1.02] ring-2 ring-accent/60" : ""}`}
                style={{ top: b.od / 60 * HODINA_PX + 1, height: vyska, background: k.bg, borderLeft: `3px solid ${k.c}`,
                  WebkitTouchCallout: "none" }}>
                {plny && (
                  <div className="flex items-center gap-2">
                    <span className="amount text-[12px] font-600" style={{ color: k.c }}>
                      {tazeny ? `${hhmm(b.od)}–${hhmm(b.do)}` : `${fmtTime(b.a.date_start)}–${fmtTime(b.a.date_end)}`}
                    </span>
                    <span className="flex-1" />
                    <span className="flex gap-[3px]">
                      {(b.a.odpovedne_osoby || []).slice(0, 3).map((u) => (
                        <span key={u} className="h-[7px] w-[7px] rounded-full border-[1.5px] border-white" style={{ background: barvaOsoby(u) }} />
                      ))}
                    </span>
                  </div>
                )}
                <div className={`text-[14px] font-700 leading-[1.3] truncate ${hotova ? "line-through text-muted" : "text-ink"}`}>
                  {!plny && <span className="amount text-[12px] font-600 mr-2" style={{ color: k.c }}>{tazeny ? hhmm(b.od) : fmtTime(b.a.date_start)}</span>}
                  {b.a.nazev}{!plny && b.a.client_name ? <span className="font-500 text-sec-3"> · {b.a.client_name}</span> : null}
                </div>
                {plny && b.a.client_name && (
                  <div className="text-[12px] leading-[1.3] text-sec-3 truncate">{[b.a.client_name, b.a.obchodni_pripad?.nazev].filter(Boolean).join(" · ")}</div>
                )}
              </div>
            );
          })}

          {dnes && ted >= 0 && ted <= konec && (
            <div className="absolute -left-1.5 right-0 border-t-[1.5px] border-accent pointer-events-none" style={{ top: ted / 60 * HODINA_PX }} data-testid="kal-ted">
              <span className="absolute -left-0.5 -top-[4.5px] h-2 w-2 rounded-full bg-accent" />
            </div>
          )}
        </div>
      </div>

      {casove.some(smiPsat) && (
        <div className="t-meta text-muted px-1 -mt-1" data-testid="kal-osa-napoveda">
          {tah ? "Posuň na nový čas a pusť." : "Událost přesuneš, když ji podržíš a posuneš."}
        </div>
      )}
      {aktivity.length === 0 && (
        <div className="card px-4 py-3.5 flex items-start gap-2.5 -mt-1" data-testid="kal-osa-prazdna">
          <span className="h-2 w-2 rounded-full bg-[#c9ced5] shrink-0 mt-1.5" />
          <div className="text-[14px] leading-[1.5] text-sec">
            Na {DNY_4[den.getDay()]} {den.getDate()}. {den.getMonth() + 1}. nemáš nic. Přidej schůzku nebo servis. Tapni do mezery v ose a čas se předvyplní.
          </div>
        </div>
      )}
    </div>
  );
}
